/**
 * AnchorsSection — the ANCHORS block of the EnvironmentInspector (ENG-590).
 *
 * The list half of the feature. Placing an anchor is a viewport gesture
 * (right-click → "Place anchor here"); this is where you see what has been
 * placed, rename it to something the character LLM will be handed as a prompt
 * token, nudge a coordinate, and delete.
 *
 * ── Save is explicit here, unlike the viewport ──────────────────────────────
 * A right-click has no Save button in front of it so it commits immediately;
 * a panel of fields does, and the repo's rule is that local edits stay dirty
 * until someone commits them. DirtyBadge carries the state, next to the thing
 * it describes (the UX proximity rule) rather than in a status bar.
 *
 * ── It edits the LOADED environment, which may not be this card ─────────────
 * The store holds ONE environment, adopted by applyEnvironment. Opening an
 * inspector for a different row must not silently retarget it — the anchors on
 * screen would be one environment's and the save another's. So the section says
 * which environment it is editing, and offers Apply as the way in, rather than
 * quietly switching the subject under the viewport's right-click.
 */
import React, { useEffect, useState } from 'react'
import DirtyBadge from '../DirtyBadge'
import { environmentAnchorStore } from '../../lib/environmentAnchorStore'
import { anchorList, labelError, nameError, normalizeAnchor } from '../../lib/environmentAnchors'

const AXES = [['px', 'X'], ['py', 'Y'], ['pz', 'Z']]

export default function AnchorsSection({ assetId, assetName, C }) {
  const [snap, setSnap] = useState(() => environmentAnchorStore.getState())
  // Names being typed live here, not in the store: a half-typed rename is not a
  // valid anchor name, and pushing each keystroke through would either be
  // refused mid-word or mint a trail of anchors.
  const [draftNames, setDraftNames] = useState({})
  // Same reason as draftNames: a half-typed label is not yet a commit.
  const [draftLabels, setDraftLabels] = useState({})

  useEffect(() => environmentAnchorStore.subscribe(() => {
    setSnap(environmentAnchorStore.getState())
  }), [])

  const isSubject = !!assetId && snap.assetId === assetId
  const list = isSubject ? anchorList(snap.anchors) : []

  const commitName = (from) => {
    const to = (draftNames[from] ?? '').trim()
    setDraftNames(d => { const n = { ...d }; delete n[from]; return n })
    if (!to || to === from) return
    environmentAnchorStore.rename(assetId, from, to)
  }

  const setAxis = (name, key, value) => {
    const next = { ...snap.anchors[name], [key]: value }
    // A cleared field is not 0 — normalizeAnchor rejects it, so the previous
    // value stays on screen until the field is a number again. Storing 0 would
    // move the anchor somewhere real and wrong.
    if (normalizeAnchor(next)) environmentAnchorStore.place(assetId, name, next)
  }

  const commitLabel = (name, value) => {
    setDraftLabels(d => { const n = { ...d }; delete n[name]; return n })
    if ((snap.anchors[name]?.label ?? '') === value.trim()) return
    environmentAnchorStore.setLabel(assetId, name, value)
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, padding: '8px 0 4px', letterSpacing: '.08em' }}>
        ANCHORS
        <DirtyBadge state={isSubject ? snap.state : 'clean'} message={snap.error ?? undefined} />
      </div>

      {!isSubject && (
        <div data-testid="anchors-not-subject" style={{ fontSize: 11, color: C.dim, lineHeight: 1.4 }}>
          {snap.assetId
            ? `Anchors are being edited on ${snap.assetName ?? 'another environment'}. Apply this one to place anchors in it.`
            : 'Apply this environment to place anchors in it.'}
        </div>
      )}

      {isSubject && (
        <>
          <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.4, marginBottom: 6 }}>
            Named world positions the character can be asked to walk to. Right-click
            the floor in the viewport to place one.
          </div>

          {list.length === 0 && (
            <div data-testid="anchors-empty" style={{ fontSize: 11, color: C.dim, fontStyle: 'italic' }}>
              None yet.
            </div>
          )}

          {list.map(({ name, ...anchor }) => {
            const draft = draftNames[name]
            const err = draft === undefined ? null : nameError(draft.trim(), snap.anchors, name)
            return (
              <div key={name} data-testid={`anchor-row-${name}`} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    data-testid={`anchor-name-${name}`}
                    value={draft ?? name}
                    onChange={e => setDraftNames(d => ({ ...d, [name]: e.target.value }))}
                    onBlur={() => commitName(name)}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                    style={{ ...nameInput, borderColor: err ? C.danger : C.border }}
                  />
                  <button
                    data-testid={`anchor-delete-${name}`}
                    onClick={() => environmentAnchorStore.remove(assetId, name)}
                    title={`Delete "${name}"`}
                    style={deleteBtn}
                  >✕</button>
                </div>
                {err && <div style={{ fontSize: 10, color: C.danger, margin: '2px 0' }}>{err}</div>}
                {/* The prose the character LLM reads. The name above is the
                    token it addresses; this is what makes the token mean
                    something. */}
                <input
                  data-testid={`anchor-label-${name}`}
                  value={draftLabels[name] ?? anchor.label ?? ''}
                  placeholder="description (optional) — e.g. the front door"
                  onChange={e => setDraftLabels(d => ({ ...d, [name]: e.target.value }))}
                  onBlur={e => commitLabel(name, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  style={{ ...labelInput, borderColor: labelError(draftLabels[name]) ? C.danger : C.border }}
                />
                <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                  {AXES.map(([key, axis]) => (
                    <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span style={{ fontSize: 9, color: C.dim }}>{axis}</span>
                      <input
                        data-testid={`anchor-${name}-${key}`}
                        type="number"
                        step={0.01}
                        value={anchor[key]}
                        onChange={e => setAxis(name, key, e.target.value)}
                        style={numInput}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )
          })}

          {snap.dropped.length > 0 && (
            // Named, never silently skipped: the server validates everything it
            // stores, so the only way to get one of these is a hand-edited row.
            <div data-testid="anchors-dropped" style={{ fontSize: 10, color: C.danger, marginTop: 4 }}>
              Unreadable and not shown: {snap.dropped.join(', ')}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              data-testid="anchors-save"
              onClick={() => environmentAnchorStore.save(assetId)}
              disabled={snap.state === 'clean' || snap.state === 'saving'}
              style={{ ...saveBtn, opacity: snap.state === 'clean' || snap.state === 'saving' ? 0.4 : 1 }}
            >{snap.state === 'saving' ? 'Saving…' : 'Save anchors'}</button>
            <button
              data-testid="anchors-revert"
              onClick={() => environmentAnchorStore.revert()}
              disabled={snap.state === 'clean' || snap.state === 'saving'}
              style={{ ...revertBtn, opacity: snap.state === 'clean' || snap.state === 'saving' ? 0.4 : 1 }}
            >Revert</button>
          </div>
          {snap.error && (
            <div data-testid="anchors-error" style={{ fontSize: 10, color: C.danger, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {snap.error}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const nameInput = {
  flex: 1, minWidth: 0, padding: '3px 6px', background: 'var(--m-bg)',
  border: '1px solid', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-mono)', fontSize: 11,
}
const labelInput = {
  width: '100%', boxSizing: 'border-box', marginTop: 3, padding: '3px 6px',
  background: 'var(--m-bg)', border: '1px solid', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-font)', fontSize: 10.5,
}
const numInput = {
  flex: 1, minWidth: 0, padding: '2px 4px', background: 'var(--m-bg)',
  border: '1px solid var(--m-line)', borderRadius: 3, color: 'inherit',
  fontFamily: 'var(--m-mono)', fontSize: 10,
}
const deleteBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 3,
  color: 'var(--m-fg-3)', cursor: 'pointer', padding: '2px 6px', fontSize: 10,
}
const saveBtn = {
  flex: 1, background: 'var(--m-accent-soft)', color: 'var(--m-accent)',
  border: '1px solid var(--m-accent)',
  borderRadius: 3, padding: '5px 8px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer',
}
const revertBtn = {
  background: 'none', border: '1px solid var(--m-line)', borderRadius: 3,
  color: 'inherit', padding: '5px 8px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer',
}
